import React from 'react';
import GeneralInfo from '../GeneralInfo/GeneralInfo';
import PrimaryContacts from '../PrimaryContacts/PrimaryContacts';
import styles from '../SlideOutPanel.module.scss';

export type SlideoutGeneralInfoSectionProps = {
  generalInfo: Record<string, any>;
  reportingStatus?: string;
  eventsData?: any[];
  isLoadingEvents?: boolean;
  eventsError?: string | null;
  parentMomentsData?: any[];
  isLoadingParentMoments?: boolean;
  parentMomentsError?: string | null;
  issueId?: string;
  onMilestoneRefresh: (patchResponse?: unknown) => void;
  onSeamlessRefresh: (patchResponse?: unknown) => void;
  onRefreshNotifications: () => void;
  onGeneralInfoChange: (updatedInfo: any) => void;
  contacts: any[];
  onRoleContactsReplace: (role: string, roleContacts: any[]) => Promise<void>;
  onContactUpdate: (updatedContact: any, index: number) => Promise<void>;
  onContactAdd: (newContact: any | any[]) => Promise<void>;
};

/**
 * Non-phased General Info + Primary Contacts wiring (Part 2B-2).
 */
export function SlideoutGeneralInfoSection({
  generalInfo,
  reportingStatus,
  eventsData,
  isLoadingEvents,
  eventsError,
  parentMomentsData,
  isLoadingParentMoments,
  parentMomentsError,
  issueId,
  onMilestoneRefresh,
  onSeamlessRefresh,
  onRefreshNotifications,
  onGeneralInfoChange,
  contacts,
  onRoleContactsReplace,
  onContactUpdate,
  onContactAdd
}: SlideoutGeneralInfoSectionProps) {
  return (
    <div className={styles.infoSection}>
      <div className={styles.generalInfoHeader}>
        <div className={styles.generalInfoGap}>
          <div className={styles.leftColumn}>
            <GeneralInfo
              data={
                {
                  ...generalInfo,
                  reportingStatus: reportingStatus ?? ''
                } as any
              }
              eventsData={eventsData}
              isLoadingEvents={isLoadingEvents}
              eventsError={eventsError}
              parentMomentsData={parentMomentsData}
              isLoadingParentMoments={isLoadingParentMoments}
              parentMomentsError={parentMomentsError}
              isPhased={false}
              issueId={issueId}
              onMilestoneRefresh={onMilestoneRefresh}
              onSeamlessRefresh={onSeamlessRefresh}
              onRefreshNotifications={onRefreshNotifications}
              onGeneralInfoChange={onGeneralInfoChange}
            />
          </div>
        </div>
      </div>
      <div className={styles.rightColumn}>
        <PrimaryContacts
          contacts={contacts}
          onRoleContactsReplace={onRoleContactsReplace}
          onContactUpdate={onContactUpdate}
          onContactAdd={onContactAdd}
        />
      </div>
    </div>
  );
}

export type SlideoutPhasedGeneralInfoProps = {
  generalInfo: Record<string, any>;
  phase: {
    showOnRoadmap?: boolean;
    inheritMilestones?: boolean;
    status?: string;
    phaseName?: string;
  };
  card: Record<string, any>;
  rootMomentName: string;
  rootMomentType: string;
  eventsData?: any[];
  isLoadingEvents?: boolean;
  eventsError?: string | null;
  parentMomentsData?: any[];
  isLoadingParentMoments?: boolean;
  parentMomentsError?: string | null;
  issueId?: string;
  phaseName?: string;
  onMilestoneRefresh: (patchResponse?: unknown) => void;
  onSeamlessRefresh: (patchResponse?: unknown) => void;
  onRefreshNotifications: () => void;
  onNotificationRefresh: (patchResponse?: unknown) => void;
  onGeneralInfoChange: (updatedInfo: any) => void;
};

/**
 * Phased General Info block (used inside phase accordion).
 */
export function SlideoutPhasedGeneralInfo({
  generalInfo,
  phase,
  card,
  rootMomentName,
  rootMomentType,
  eventsData,
  isLoadingEvents,
  eventsError,
  parentMomentsData,
  isLoadingParentMoments,
  parentMomentsError,
  issueId,
  phaseName,
  onMilestoneRefresh,
  onSeamlessRefresh,
  onRefreshNotifications,
  onNotificationRefresh,
  onGeneralInfoChange
}: SlideoutPhasedGeneralInfoProps) {
  return (
    <GeneralInfo
      data={
        {
          ...generalInfo,
          momentType: card?.momentType || generalInfo?.momentType,
          showOnRoadmap: phase.showOnRoadmap,
          inheritMilestones: phase.inheritMilestones,
          reportingStatus: phase.status ?? ''
        } as any
      }
      isCollapsible={false}
      showContent={true}
      eventsData={eventsData}
      isLoadingEvents={isLoadingEvents}
      eventsError={eventsError}
      parentMomentsData={parentMomentsData}
      isLoadingParentMoments={isLoadingParentMoments}
      parentMomentsError={parentMomentsError}
      isPhased={true}
      issueId={issueId}
      phaseName={phaseName}
      rootMomentName={rootMomentName}
      rootMomentType={rootMomentType}
      onMilestoneRefresh={onMilestoneRefresh}
      onSeamlessRefresh={onSeamlessRefresh}
      onRefreshNotifications={onRefreshNotifications}
      onGeneralInfoChange={onGeneralInfoChange}
      onNotificationRefresh={onNotificationRefresh}
    />
  );
}
